// Layer 4 (UI) — the three tab bodies' tables (locked comp `planning.html` A):
// monthly plan vs actual vs budget, the annual plan structure with its bullet
// column, and the Year-1 close by scenario with the budget foot row. Card and
// table grammar are Downtime's (`DowntimeCard`, `CARD_TABLE`, `HOT_ROW`).
// Renders PlanningTables.transform.ts; derives nothing.

import { Table, TBody, TD, TH, THead, TR } from '@/design-system';
import { cn } from '@/shared/cn';
import { ProvenanceChip } from '@/versions/v1/shared/ProvenanceChip';
import { ActorChip } from '../advisory/PipelineStrip';
import { CARD_TABLE, DowntimeCard, HOT_ROW, SYN_CHIP } from '../downtime/DowntimeCard';
import type { AnnualTableRow, MonthlyTableRead, ScenarioTableRead } from './PlanningTables.transform';
import { planningProvenanceChip } from './Scene.transform';

const WARN = '!text-[var(--state-warning)] font-semibold';
const FIGURE = '!text-ink-primary font-semibold';
const CURRENT_ROW = cn(HOT_ROW, '[&>td:first-child]:shadow-[inset_3px_0_0_var(--color-accent-500)]');

/** Signed variance as a diverging bar: below budget grows left in warning,
 *  above grows right in ink-secondary. */
function DivergingBar({ value, share }: { value: number; share: number }) {
  return (
    <span aria-hidden="true" className="grid h-2.5 w-16 grid-cols-2 items-center">
      <span className="flex h-2.5 items-center justify-end border-r border-[var(--hairline-strong)]">
        {value < 0 ? <span className="block h-1.5 rounded-[2px] bg-[var(--state-warning)]" style={{ width: `${share}%` }} /> : null}
      </span>
      <span className="flex h-2.5 items-center">
        {value >= 0 ? <span className="block h-1.5 rounded-[2px] bg-ink-secondary" style={{ width: `${share}%` }} /> : null}
      </span>
    </span>
  );
}

export function MonthlyPlanTable({ read, className }: { read: MonthlyTableRead; className?: string }) {
  const merged = read.planEqualsBudget;
  return (
    <DowntimeCard
      title="Monthly plan vs actual vs budget"
      meta={read.capDecode}
      actions={SYN_CHIP}
      className={className}
      aria-label="Monthly plan vs actual vs budget"
    >
      <div className={CARD_TABLE} data-planning-table="monthly">
        <Table wrap={false} className="tabular-nums">
          <THead>
            <TR>
              <TH>Month</TH>
              <TH className="text-right">{merged ? 'Budget · plan' : 'Budget'}</TH>
              {merged ? null : <TH className="text-right">Plan</TH>}
              <TH className="text-right">Actual</TH>
              <TH className="text-right">vs budget</TH>
              <TH className="text-right">Zn rec budget</TH>
              <TH className="text-right">Zn rec actual</TH>
              <TH className="text-right">Δ Zn</TH>
            </TR>
          </THead>
          <TBody>
            {read.rows.map((row) => (
              <TR key={row.id} className={row.current ? CURRENT_ROW : undefined} data-month-row={row.id}>
                <TD className="whitespace-nowrap font-medium !text-ink-primary">{row.label}</TD>
                <TD className="text-right">{row.budget}</TD>
                {merged ? null : <TD className="text-right">{row.plan}</TD>}
                <TD className="whitespace-nowrap text-right">
                  {row.actual === undefined ? null : row.actualNote ? (
                    <>
                      <span className={FIGURE}>{row.actual}</span>
                      <span className="block text-[11px] leading-tight text-muted">{row.actualNote}</span>
                    </>
                  ) : row.actual}
                </TD>
                <TD className="whitespace-nowrap text-right">
                  {row.vsBudgetPct === undefined || row.vsBudget === undefined ? null : (
                    <span className="inline-flex items-center justify-end gap-2">
                      <DivergingBar value={row.vsBudgetPct} share={row.vsBudgetBar ?? 0} />
                      <span className={cn('w-12', row.vsBudgetPct < 0 ? WARN : FIGURE)}>{row.vsBudget}</span>
                    </span>
                  )}
                </TD>
                <TD className="text-right">{row.znBudget}</TD>
                <TD className="text-right">{row.znActual}</TD>
                <TD className={cn('whitespace-nowrap text-right', row.znDeltaPpt !== undefined && row.znDeltaPpt < 0 ? WARN : undefined)}>{row.znDelta}</TD>
              </TR>
            ))}
            <TR data-month-row="total" className="border-t border-[var(--hairline-strong)]">
              <TD className={cn('whitespace-nowrap', FIGURE)}>{read.total.label}</TD>
              <TD className={cn('text-right', FIGURE)}>{read.total.budget}</TD>
              {merged ? null : <TD className={cn('text-right', FIGURE)}>{read.total.plan}</TD>}
              <TD className={cn('text-right', FIGURE)}>{read.total.actual}</TD>
              <TD className={cn('text-right', read.total.vsBudgetPct < 0 ? WARN : FIGURE)}>{read.total.vsBudget}</TD>
              <TD className={cn('text-right', FIGURE)}>{read.total.znBudget}</TD>
              <TD className={cn('text-right', FIGURE)}>{read.total.znActual}</TD>
              <TD className={cn('whitespace-nowrap text-right', FIGURE)}>{read.total.znDelta}</TD>
            </TR>
          </TBody>
        </Table>
      </div>
    </DowntimeCard>
  );
}

function Bullet({ bullet, below }: { bullet: NonNullable<AnnualTableRow['bullet']>; below: boolean }) {
  return (
    // K16 round 2: the bullet flexes with its column (min 4 rem) so the table
    // fits the 8/4 panel from 1400 px without a sideways scroll.
    <span aria-hidden="true" className="relative block h-3.5 w-full min-w-16">
      <span className="absolute inset-x-0 top-1 h-1.5 rounded-full bg-[var(--hairline)]" />
      <span
        className="absolute left-0 top-1 h-1.5 rounded-full"
        style={{ width: `${bullet.toDatePct}%`, background: below ? 'var(--state-warning)' : 'var(--ink-primary)' }}
      />
      <span className="absolute top-0 h-3.5 w-0.5 bg-ink-secondary" style={{ left: `calc(${bullet.budgetPct}% - 1px)` }} />
      <span className="absolute top-0 h-3.5 border-l-[1.5px] border-dashed border-[var(--ink-tertiary)]" style={{ left: `${bullet.stretchPct}%` }} />
    </span>
  );
}

export function AnnualPlanTable({ rows, className }: { rows: readonly AnnualTableRow[]; className?: string }) {
  return (
    <DowntimeCard
      title="Annual plan structure"
      meta={
        <span className="inline-flex items-center gap-2.5">
          <span className="inline-flex items-center gap-1"><span aria-hidden="true" className="inline-block w-3 border-t-2 border-ink-primary" />Year 1 to date</span>
          <span className="inline-flex items-center gap-1"><span aria-hidden="true" className="inline-block h-2.5 w-0.5 bg-ink-secondary" />budget</span>
          <span className="inline-flex items-center gap-1"><span aria-hidden="true" className="inline-block h-2.5 border-l-[1.5px] border-dashed border-[var(--ink-tertiary)]" />stretch</span>
          <span>scale = design</span>
        </span>
      }
      className={className}
      aria-label="Annual plan structure"
    >
      <div className={CARD_TABLE} data-planning-table="annual">
        <Table wrap={false} className="tabular-nums">
          <THead>
            <TR>
              <TH>Metric</TH>
              <TH className="text-right">Design</TH>
              <TH className="text-right">Annual budget</TH>
              <TH className="text-right">Stretch</TH>
              <TH className="text-right">Year 1 to date</TH>
              <TH className="w-full"><span className="sr-only">Year 1 to date against budget and stretch</span></TH>
            </TR>
          </THead>
          <TBody>
            {rows.map((row) => (
              <TR key={row.id} data-annual-row={row.id}>
                <TD className="whitespace-nowrap" title={row.provenance}>
                  <span className="inline-flex items-center gap-1.5">
                    <span>
                      <span className="font-medium !text-ink-primary">{row.label}</span>{' '}
                      <span className="text-muted">({row.unit})</span>
                    </span>
                    <ProvenanceChip {...planningProvenanceChip(row.provenance)} compact />
                  </span>
                </TD>
                <TD className="text-right">{row.design}</TD>
                <TD className="whitespace-nowrap text-right">
                  {row.budget}
                  {row.budgetNote ? <span className="block text-[11px] leading-tight text-muted">{row.budgetNote}</span> : null}
                </TD>
                <TD className="text-right">{row.stretch}</TD>
                <TD className={cn('text-right', row.belowBudget ? WARN : FIGURE)}>{row.toDate}</TD>
                <TD className="w-full">{row.bullet ? <Bullet bullet={row.bullet} below={row.belowBudget} /> : null}</TD>
              </TR>
            ))}
          </TBody>
        </Table>
      </div>
    </DowntimeCard>
  );
}

export function ScenarioTable({ read, className }: { read: ScenarioTableRead; className?: string }) {
  return (
    <DowntimeCard
      title="Year 1 close by scenario"
      meta={read.formula}
      actions={<ActorChip actor="Engine">AI suggestion</ActorChip>}
      className={className}
      aria-label="Year 1 close by scenario"
    >
      <div className={CARD_TABLE} data-planning-table="forecast">
        <Table wrap={false} className="tabular-nums">
          <THead>
            <TR>
              <TH>Scenario</TH>
              <TH className="text-right">Full-year feed <span className="font-medium normal-case tracking-normal">dst</span></TH>
              <TH><span className="sr-only">Full-year feed against budget and stretch</span></TH>
              <TH className="text-right">vs budget</TH>
              <TH className="text-right !whitespace-normal">Zn rec (rest-of-year avg)</TH>
              <TH className="text-right">Full-year NSR</TH>
              <TH className="text-right">vs budget NSR</TH>
            </TR>
          </THead>
          <TBody>
            {read.rows.map((row) => {
              const central = row.name === 'Central';
              return (
                <TR key={row.name} data-scenario-row={row.name} title={row.recoveryBasis}>
                  <TD className="whitespace-nowrap">
                    <span className="font-medium !text-ink-primary">{row.name}</span>
                    <span className="block text-[11px] leading-tight text-muted">{row.rate} · {row.basis}</span>
                  </TD>
                  <TD className={cn('text-right', central ? FIGURE : '!text-ink-primary font-medium')}>{row.tonnes}</TD>
                  <TD>
                    <span aria-hidden="true" className="relative block h-3.5 w-28 min-[1400px]:w-52">
                      <span className="absolute inset-x-0 top-1 h-1.5 rounded-full bg-[var(--hairline)]" />
                      <span className="absolute left-0 top-1 h-1.5 rounded-full" style={{ width: `${row.barPct}%`, background: central ? 'var(--ink-primary)' : 'var(--ink-secondary)' }} />
                      <span className="absolute top-0 h-3.5 w-0.5 bg-ink-secondary" style={{ left: `${read.budgetTickPct}%` }} />
                      <span className="absolute top-0 h-3.5 border-l-[1.5px] border-dashed border-[var(--ink-tertiary)]" style={{ left: `${read.stretchTickPct}%` }} />
                    </span>
                  </TD>
                  <TD className="text-right">{row.vsBudget}</TD>
                  <TD className="text-right">{row.znRec}</TD>
                  <TD className={cn('text-right', central ? FIGURE : '!text-ink-primary font-medium')}>{row.nsr}</TD>
                  <TD className="text-right">{row.nsrVsBudget}</TD>
                </TR>
              );
            })}
            <TR data-scenario-row="Budget" className="border-t border-[var(--hairline-strong)]">
              <TD className="whitespace-nowrap">
                <span className={FIGURE}>Budget</span>
                <span className="block text-[11px] leading-tight text-muted">monthly budget recoveries</span>
              </TD>
              <TD className={cn('text-right', FIGURE)}>{read.budget.tonnes}</TD>
              <TD />
              <TD />
              <TD />
              <TD className={cn('text-right', FIGURE)}>{read.budget.nsr}</TD>
              <TD />
            </TR>
          </TBody>
        </Table>
      </div>
      <p className="mt-1.5 text-xs text-muted">{read.domainDecode}</p>
    </DowntimeCard>
  );
}
